import { maxBy, minBy } from 'es-toolkit';
import React from 'react';
import { connect, ResolveThunks } from 'react-redux';
import { Box, Divider, Typography } from '@mui/material';
import { Draft, produce } from 'immer';

import { PropertySelectField, PropertyTextInput } from '@paragon/client-tools';
import { Component, isTruss, LoadDirection, ManualLoadDefinition } from '@paragon/contract-library';
import { LineSegment2D } from '@paragon/geometry-library';
import { PropertiesPanel, PropertiesSubpanel } from '@paragon/layout-models';
import { enqueueError } from '@paragon/notification-tools';
import { calculateMemberMidline } from '@paragon/three-shared';
import { assertExhaustiveSwitch, humanCase, notNullish } from '@paragon/utilities';

import { RootState } from '../../../../store';
import { finishInsertingParapetManualLoadSettings } from '../../../../store/actions/insertActions';
import { getActiveComponentDesign } from '../../../../store/selectors/getActiveComponentDesignSelector';
import InsertionPanelContainer from './InsertionPanelContainer';
import ParapetLoadInsertionFields, { ParapetLoadDirection, ParapetLoadFields } from './ParapetLoadInsertionFields';

export const directionToLoadDirection = (direction: ParapetLoadDirection): LoadDirection => {
  switch (direction) {
    case 'Left':
      return 'GlobalLeft';
    case 'Right':
      return 'GlobalRight';
    default:
      assertExhaustiveSwitch(direction);
  }
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ResolveThunks<typeof actionCreators>;
type Props = StateProps & DispatchProps;

interface LoadCaseContainer {
  a: ParapetLoadFields[];
  b: ParapetLoadFields[];
}

type State = {
  loadCases: LoadCaseContainer;

  loadMemberType: 'LeftEndVertical' | 'RightEndVertical' | null;
  name: string | null;
};

const defaultParapetLoadFields: ParapetLoadFields = {
  startMagnitude: 0,
  endMagnitude: 0,
  magnitudeUnit: 'PoundsPerSquareFoot',
  direction: 'Left',
  startY: 0,
  endY: 0,
};

function calculateMemberMidlineForEndVertical(
  component: Component,
  loadMemberType: 'LeftEndVertical' | 'RightEndVertical',
) {
  const endVerticalMidlines = component.members
    .filter((member) => member.memberType === 'EndVertical')
    .map((member) => {
      const midline3D = calculateMemberMidline(member, component);
      if (!midline3D) {
        console.error('Could not find midline for member.');
        return null;
      }
      return LineSegment2D.fromPointObjects(midline3D.start, midline3D.end);
    })
    .filter(notNullish);

  const midlineForLoadMemberType =
    loadMemberType === 'LeftEndVertical'
      ? minBy(endVerticalMidlines, (midline) => midline.basePoint.y)
      : maxBy(endVerticalMidlines, (midline) => midline.basePoint.y);

  if (!midlineForLoadMemberType) return null;

  return midlineForLoadMemberType.basePoint.y < midlineForLoadMemberType.endPoint.y
    ? midlineForLoadMemberType.reverse()
    : midlineForLoadMemberType;
}

class ParapetWindLoadSettingsInsertion extends React.Component<Props, State> {
  readonly state: State = {
    loadCases: {
      a: [
        {
          ...defaultParapetLoadFields,
          direction: 'Right',
          endY: 24,
        },
        {
          ...defaultParapetLoadFields,
          startY: 24,
        },
      ],
      b: [
        {
          ...defaultParapetLoadFields,
          endY: 24,
        },
        {
          ...defaultParapetLoadFields,
          direction: 'Right',
          startY: 24,
        },
      ],
    },
    loadMemberType: null,
    name: null,
  };

  onChangeParapetLoadField =
    (loadCase: keyof LoadCaseContainer, index: number) =>
    <T extends keyof ParapetLoadFields>(property: T) =>
    (value: any) => {
      this.setState(
        produce((draft: State) => {
          draft.loadCases[loadCase][index][property] = value;
        }),
      );
    };

  onChangeLoadMemberType = (loadMemberType: 'LeftEndVertical' | 'RightEndVertical') => {
    const { componentDesign } = this.props;
    if (!componentDesign) return;
    const midline = calculateMemberMidlineForEndVertical(componentDesign.component, loadMemberType);

    this.setState(
      produce((state: Draft<State>) => {
        state.loadMemberType = loadMemberType;

        if (midline) {
          for (const loadCase of Object.values(state.loadCases)) {
            loadCase[0].startY = midline.basePoint.y;
            loadCase[0].endY = 24;

            loadCase[1].startY = 24;
            loadCase[1].endY = midline.endPoint.y;
          }
        }
      }),
    );
  };

  parapetLoadCaseToMutableManualLoadArray = (
    parapetLoadCase: ParapetLoadFields[],
  ): ManualLoadDefinition[] | undefined => {
    const { loadMemberType } = this.state;
    if (loadMemberType == null) return undefined;

    return parapetLoadCase.map((parapetLoadFields) => ({
      ...parapetLoadFields,

      guid: crypto.randomUUID(),
      definitionAxis: 'Y',
      startCoordinate: parapetLoadFields.startY,
      endCoordinate: parapetLoadFields.endY,
      magnitudeUnit: parapetLoadFields.magnitudeUnit,
      isProjected: false,
      loadDirection: directionToLoadDirection(parapetLoadFields.direction),
      loadType: 'Wind',
      memberType: loadMemberType,
      distribution: 'Distributed',
      name: null,
    }));
  };

  insertParapetWindLoadSettings = () => {
    const loadCaseALoads = this.parapetLoadCaseToMutableManualLoadArray(this.state.loadCases.a);
    const loadCaseBLoads = this.parapetLoadCaseToMutableManualLoadArray(this.state.loadCases.b);
    if (!loadCaseALoads || !loadCaseBLoads) {
      this.props.enqueueError('Error preparing Parapet loads.');
      return;
    }
    this.props.finishInsertingParapetManualLoadSettings({
      guid: crypto.randomUUID(),
      loadCaseALoads,
      loadCaseBLoads,
      name: this.state.name,
    });
  };

  render() {
    const { componentDesign } = this.props;
    if (!componentDesign) return null;

    const { component } = componentDesign;
    if (!isTruss(component)) return null;

    return (
      <InsertionPanelContainer
        disabled={this.state.loadMemberType == null}
        onInsert={this.insertParapetWindLoadSettings}
      >
        <PropertiesPanel title="Add PAL Loads">
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <PropertySelectField
              sx={{ m: 1, flex: 1 }}
              property="Load Member Type"
              value={this.state.loadMemberType}
              options={['LeftEndVertical', 'RightEndVertical']}
              getName={(loadMemberType) => humanCase(loadMemberType)}
              submit={this.onChangeLoadMemberType}
            />
            <PropertyTextInput
              sx={{ m: 1, flex: 1 }}
              property="PAL Load Name (Optional)"
              value={this.state.name ?? ''}
              submit={(name) =>
                this.setState({
                  name: name === '' ? null : name,
                })
              }
            />
          </Box>
          <Box sx={{ display: 'flex' }}>
            <PropertiesSubpanel title="Load Case A">
              <Typography variant="caption" sx={{ display: 'block' }}>
                First case:
              </Typography>
              <ParapetLoadInsertionFields
                endMagnitudeDisabled
                loadMemberTypeIsSelected={this.state.loadMemberType != null}
                parapetLoadFields={this.state.loadCases.a[0]}
                trussSpacing={component.spacing}
                onChange={this.onChangeParapetLoadField('a', 0)}
              />
              <Divider sx={{ m: 1 }} />
              <Typography variant="caption" sx={{ display: 'block' }}>
                Second case:
              </Typography>
              <ParapetLoadInsertionFields
                startMagnitudeDisabled
                loadMemberTypeIsSelected={this.state.loadMemberType != null}
                parapetLoadFields={this.state.loadCases.a[1]}
                trussSpacing={component.spacing}
                onChange={this.onChangeParapetLoadField('a', 1)}
              />
            </PropertiesSubpanel>
            <PropertiesSubpanel title="Load Case B">
              <Typography variant="caption" sx={{ display: 'block' }}>
                First case:
              </Typography>
              <ParapetLoadInsertionFields
                endMagnitudeDisabled
                loadMemberTypeIsSelected={this.state.loadMemberType != null}
                parapetLoadFields={this.state.loadCases.b[0]}
                trussSpacing={component.spacing}
                onChange={this.onChangeParapetLoadField('b', 0)}
              />
              <Divider sx={{ m: 1 }} />
              <Typography variant="caption" sx={{ display: 'block' }}>
                Second case:
              </Typography>
              <ParapetLoadInsertionFields
                startMagnitudeDisabled
                loadMemberTypeIsSelected={this.state.loadMemberType != null}
                parapetLoadFields={this.state.loadCases.b[1]}
                trussSpacing={component.spacing}
                onChange={this.onChangeParapetLoadField('b', 1)}
              />
            </PropertiesSubpanel>
          </Box>
        </PropertiesPanel>
      </InsertionPanelContainer>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  componentDesign: getActiveComponentDesign(state),
});

const actionCreators = {
  enqueueError,
  finishInsertingParapetManualLoadSettings,
};

export default connect(mapStateToProps, actionCreators)(ParapetWindLoadSettingsInsertion);
